/**
 * Full MVP inventory modal — large dialog with category tabs
 * (Quality / Population health / Cost / PI / IA).
 * QCDR quality rows stay informational-only (registry does not calculate them).
 */
import { useEffect, useMemo, useState } from 'react';
import { MipsDataService, type MvpDefinition } from '@/lib/mips-data-service';
import { isQcdrMeasureId } from '@/lib/mvp/quality';
import {
  mapAllValidationCriteria,
  type CMSValidationCriteria,
  type MappedValidationCriteria,
} from '@/lib/ia-data-utils';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { CmsMeasure } from '@/lib/mips-data-service';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Info } from 'lucide-react';
import iaCriteria2025 from '@/data/2025-mips-data/cms_2025_ia_validation_criteria.json';
import iaCriteria2026 from '@/data/2026-mips-data/cms_2026_ia_validation_criteria.json';

type CategoryTab = 'quality' | 'popHealth' | 'cost' | 'pi' | 'ia';

type SpecLink = { label: string; url: string };

const IA_CRITERIA_BY_YEAR: Record<number, MappedValidationCriteria[]> = {
  2025: mapAllValidationCriteria(iaCriteria2025 as CMSValidationCriteria[]),
  2026: mapAllValidationCriteria(iaCriteria2026 as CMSValidationCriteria[]),
};

function iaCriteriaMapForYear(year: number): Map<string, MappedValidationCriteria> {
  const rows = IA_CRITERIA_BY_YEAR[year] || IA_CRITERIA_BY_YEAR[2025] || [];
  return new Map(rows.map(r => [r.measureId, r]));
}

/** Split CMS "Objective & Validation Documentation" into display sections. */
function parseValidationCriteria(combinedText: string | undefined) {
  if (!combinedText) return { objective: '', validationDoc: '' };
  const objectiveMatch = combinedText.match(/^Objective:\s*([\s\S]*?)(?=\n\nValidation Documentation:|$)/i);
  const validationMatch = combinedText.match(/Validation Documentation:\s*([\s\S]*?)(?=\n\nInformation:|$)/i);
  return {
    objective: objectiveMatch ? objectiveMatch[1].trim() : '',
    validationDoc: validationMatch ? validationMatch[1].trim() : '',
  };
}

/** Amber requirements tip — compact, always visible under the active tab. */
function CategoryTip({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Alert className="border-amber-500/30 bg-amber-500/5 py-2 px-3 [&>svg]:top-2.5 [&>svg]:left-3">
      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
      <AlertTitle className="text-xs font-semibold text-amber-800 mb-0">
        {title}
      </AlertTitle>
      <AlertDescription className="text-xs text-amber-700/90 leading-snug mt-0.5 [&_strong]:text-amber-900">
        {children}
      </AlertDescription>
    </Alert>
  );
}

function ValidationCriteriaBox({ combinedText }: { combinedText?: string }) {
  const { objective, validationDoc } = parseValidationCriteria(combinedText);
  if (!objective && !validationDoc) {
    // Fall back to raw text if CMS format differs
    if (!combinedText?.trim()) return null;
    return (
      <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
          <Info className="w-4 h-4" />
          CMS Validation Criteria
        </div>
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{combinedText}</p>
      </div>
    );
  }
  return (
    <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <Info className="w-4 h-4" />
        CMS Validation Criteria
      </div>
      {objective && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Objective</div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{objective}</p>
        </div>
      )}
      {validationDoc && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
            Validation Documentation
          </div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{validationDoc}</p>
        </div>
      )}
    </div>
  );
}

function collectionTypeLabel(methods?: string[]): string {
  if (!methods?.length) return '—';
  return methods.map(m => {
    const s = String(m);
    if (s === 'administrativeClaims') return 'Administrative Claims';
    if (s === 'registry' || s === 'mipsCqm') return 'MIPS CQM';
    if (s === 'electronicHealthRecord' || s === 'eCqm') return 'eCQM';
    if (s === 'claims') return 'Claims';
    if (s === 'cahps') return 'CAHPS';
    return s.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
  }).join(', ');
}

/** Spec PDFs/pages from cms_*_mips_measures.json measureSpecification. */
function specLinksFor(measure: CmsMeasure | undefined, measureId: string, year: number): SpecLink[] {
  const spec = measure?.measureSpecification;
  const links: SpecLink[] = [];
  if (spec?.registry) links.push({ label: 'MIPS CQM / Registry', url: spec.registry });
  if (spec?.electronicHealthRecord) links.push({ label: 'eCQM', url: spec.electronicHealthRecord });
  if (spec?.claims) links.push({ label: 'Claims', url: spec.claims });
  if (spec?.default) links.push({ label: 'Specification', url: spec.default });
  // IA uses embedded validation criteria instead of external links
  if (links.length === 0 && !measureId.startsWith('IA_')) {
    const tab = measureId.startsWith('PI_')
      ? 'promotingInteroperability'
      : (measureId.startsWith('COST_') || measure?.category === 'cost')
        ? 'costMeasures'
        : 'qualityMeasures';
    links.push({
      label: 'View on QPP',
      url: `https://qpp.cms.gov/mips/explore-measures?tab=${tab}&py=${year}&search=${encodeURIComponent(measureId)}`,
    });
  }
  return links;
}

const pillClass =
  'rounded-full bg-slate-100 text-slate-700 border-slate-200/80 whitespace-nowrap px-2.5 py-1 text-xs font-medium';

function ExpandRow({
  id,
  title,
  collectionType,
  subtitle,
  description,
  extraBadges,
  specLinks,
  validationDocumentation,
}: {
  id: string;
  title: string;
  /** CMS submission methods — shown as right-side "Collection Type: [pills]" */
  collectionType?: string;
  /** Secondary line (e.g. Type / Subcategory) when not covered by collection type */
  subtitle?: string;
  description?: string | null;
  extraBadges?: React.ReactNode;
  specLinks?: SpecLink[];
  /** IA: CMS Objective & Validation Documentation (replaces spec links) */
  validationDocumentation?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const hasSpecs = (specLinks?.length ?? 0) > 0;
  const hasValidation = !!(validationDocumentation && validationDocumentation.trim());
  const typePills = (collectionType && collectionType !== '—')
    ? collectionType.split(',').map(s => s.trim()).filter(Boolean)
    : [];
  const hasBody =
    !!(description && description.trim()) || !!subtitle || hasSpecs || hasValidation;
  return (
    <div className="rounded-md border bg-white">
      <button
        type="button"
        className="w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-slate-50/80 disabled:opacity-100"
        onClick={() => hasBody && setOpen(o => !o)}
        disabled={!hasBody}
      >
        {hasBody ? (
          open ? <ChevronDown className="h-4 w-4 mt-3 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 mt-3 shrink-0 text-muted-foreground" />
        ) : (
          <span className="w-4 mt-3 shrink-0" />
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Measure ID
              </div>
              <div className="text-xl font-black text-primary leading-none mt-0.5 tabular-nums">
                {id}
              </div>
            </div>
            {typePills.length > 0 && (
              <div className="shrink-0 text-right">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Collection Type
                </div>
                <div className="flex flex-wrap justify-end gap-1.5 mt-1">
                  {typePills.map(t => (
                    <Badge key={t} variant="secondary" className={pillClass}>
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="text-sm font-medium text-foreground leading-snug">{title}</div>
          {(extraBadges || (hasSpecs && !open) || (hasValidation && !open) || (subtitle && !open)) && (
            <div className="flex flex-wrap items-center gap-2">
              {extraBadges}
              {subtitle && !open && (
                <span className="text-xs text-muted-foreground">{subtitle}</span>
              )}
              {hasValidation && !open && (
                <span className="text-[10px] text-primary/80 inline-flex items-center gap-0.5">
                  <Info className="h-3 w-3" /> Validation criteria
                </span>
              )}
              {hasSpecs && !open && !hasValidation && (
                <span className="text-[10px] text-primary/80 inline-flex items-center gap-0.5">
                  <ExternalLink className="h-3 w-3" /> Spec
                </span>
              )}
            </div>
          )}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pl-10 space-y-2 border-t bg-slate-50/40">
          {subtitle && (
            <p className="text-xs text-muted-foreground pt-2">{subtitle}</p>
          )}
          {description && (
            <p className="text-sm text-foreground/90 leading-relaxed pt-1">{description}</p>
          )}
          {hasValidation && (
            <ValidationCriteriaBox combinedText={validationDocumentation!} />
          )}
          {hasSpecs && !hasValidation && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
              {specLinks!.map(link => (
                <a
                  key={link.url + link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink className="h-3 w-3" />
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function rowsFromIds(ids: string[], dataService: MipsDataService, year: number) {
  return ids.map(id => {
    const m = dataService.getCmsMeasure(id);
    return {
      id,
      title: m?.title || 'Title unavailable',
      description: m?.description || null,
      collectionType: collectionTypeLabel(m?.submissionMethods),
      measureType: m?.measureType || null,
      subcategory: m?.subcategoryId || null,
      qcdr: isQcdrMeasureId(id),
      specLinks: specLinksFor(m, id, year),
      validationDocumentation: null as string | null,
    };
  });
}

/** IA rows: titles/descriptions from validation-criteria file; no external spec links. */
function iaRowsFromIds(ids: string[], dataService: MipsDataService, year: number) {
  const criteria = iaCriteriaMapForYear(year);
  return ids.map(id => {
    const ia = criteria.get(id);
    const m = dataService.getCmsMeasure(id);
    return {
      id,
      title: ia?.activityName || m?.title || 'Title unavailable',
      description: ia?.activityDescription || m?.description || null,
      collectionType: collectionTypeLabel(m?.submissionMethods),
      measureType: null as string | null,
      subcategory: ia?.subcategoryName || m?.subcategoryId || null,
      qcdr: false,
      specLinks: [] as SpecLink[],
      validationDocumentation: ia?.validationDocumentation || null,
    };
  });
}

export function MvpDetailDialog({
  mvp,
  programYear,
  open,
  onOpenChange,
}: {
  mvp: MvpDefinition | null;
  programYear: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const dataService = MipsDataService.getInstance();
  const [tab, setTab] = useState<CategoryTab>('quality');
  const [specialtiesOpen, setSpecialtiesOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('quality');
      setSpecialtiesOpen(false);
    }
  }, [open, mvp?.mvpId]);

  const qualityRows = useMemo(
    () => (mvp ? rowsFromIds(mvp.qualityMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );
  const foundationRows = useMemo(
    () => (mvp ? rowsFromIds(mvp.foundationQualityMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );
  const adminClaimsRows = useMemo(
    () => (mvp ? rowsFromIds(mvp.administrativeClaimsMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );
  const costRows = useMemo(
    () => (mvp ? rowsFromIds(mvp.costMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );
  const piRows = useMemo(
    () => (mvp ? rowsFromIds(mvp.foundationPiMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );
  const iaRows = useMemo(
    () => (mvp ? iaRowsFromIds(mvp.iaMeasureIds, dataService, programYear) : []),
    [mvp, dataService, programYear],
  );

  if (!mvp) return null;

  const tabBtn =
    'px-3 py-2 text-sm font-medium rounded-md border transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=inactive]:bg-white data-[state=inactive]:text-gray-700 data-[state=inactive]:border-gray-200 data-[state=inactive]:hover:bg-gray-50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[70rem] w-[min(96vw,70rem)] h-[min(92vh,57rem)] overflow-hidden flex flex-col gap-0 p-0">
        <Tabs
          value={tab}
          onValueChange={v => setTab(v as CategoryTab)}
          className="flex flex-col flex-1 min-h-0"
        >
          <DialogHeader className="px-6 pt-5 pb-0 border-b shrink-0 space-y-0 text-left">
            {/* pr-14 clears the absolute close (X) at right-4; ID sits left of X */}
            <div className="flex items-start justify-between gap-4 pr-14 pb-3">
              <div className="min-w-0 flex-1 space-y-2">
                <DialogTitle className="text-2xl font-semibold leading-snug tracking-tight pr-2">
                  {mvp.title}
                </DialogTitle>
                {(mvp.clinicalTopic || mvp.description) && (
                  <DialogDescription asChild>
                    <div className="space-y-1.5 border-l-2 border-slate-200 pl-3">
                      {mvp.clinicalTopic && (
                        <p className="text-xs font-medium text-foreground/70 tracking-wide">
                          {mvp.clinicalTopic}
                        </p>
                      )}
                      {mvp.description && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {mvp.description}
                        </p>
                      )}
                    </div>
                  </DialogDescription>
                )}
                {mvp.specialtiesMostApplicableTo.length > 0 && (
                  <div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setSpecialtiesOpen(v => !v)}
                      aria-expanded={specialtiesOpen}
                    >
                      {specialtiesOpen
                        ? <ChevronDown className="h-3.5 w-3.5" />
                        : <ChevronRight className="h-3.5 w-3.5" />}
                      <span className="font-medium text-foreground/80">Suggested specialties</span>
                      <span className="tabular-nums">({mvp.specialtiesMostApplicableTo.length})</span>
                      <span className="text-muted-foreground/70">· CMS guidance; any MVP allowed</span>
                    </button>
                    {specialtiesOpen && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5 pl-4">
                        {mvp.specialtiesMostApplicableTo.map(s => (
                          <Badge
                            key={s}
                            variant="secondary"
                            className="font-normal text-xs px-2 py-0.5 bg-slate-100 text-slate-700 hover:bg-slate-100"
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Badge className="shrink-0 bg-slate-800 text-white hover:bg-slate-800 font-mono text-sm font-semibold tracking-wide px-3 py-1.5 mt-0.5">
                {mvp.mvpId}
              </Badge>
            </div>

            <TabsList className="h-auto w-full flex flex-wrap justify-start gap-2 bg-transparent p-0 pb-3">
              <TabsTrigger value="quality" className={tabBtn}>
                Quality
                <span className="ml-1.5 tabular-nums opacity-80">({qualityRows.length})</span>
              </TabsTrigger>
              <TabsTrigger value="popHealth" className={tabBtn}>
                Population health
                <span className="ml-1.5 tabular-nums opacity-80">({foundationRows.length})</span>
              </TabsTrigger>
              <TabsTrigger value="cost" className={tabBtn}>
                Cost
                <span className="ml-1.5 tabular-nums opacity-80">({costRows.length})</span>
              </TabsTrigger>
              <TabsTrigger value="pi" className={tabBtn}>
                PI
                <span className="ml-1.5 tabular-nums opacity-80">({piRows.length})</span>
              </TabsTrigger>
              <TabsTrigger value="ia" className={tabBtn}>
                IA
                <span className="ml-1.5 tabular-nums opacity-80">({iaRows.length})</span>
              </TabsTrigger>
            </TabsList>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            <TabsContent value="quality" className="mt-0 space-y-3 focus-visible:ring-0">
              <CategoryTip title="Quality measures in this MVP">
                Expand a row for description and specification links.
                Population health (479/484) is under its own tab.
                {mvp.hasCahps && <> This MVP includes CAHPS — register by <strong>Jun 30</strong>.</>}
              </CategoryTip>
              <div className="space-y-1.5">
                {qualityRows.map(r => (
                  <ExpandRow
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    collectionType={r.collectionType}
                    description={r.description}
                    specLinks={r.specLinks}
                    extraBadges={
                      r.qcdr ? (
                        <Badge variant="outline" className="text-[10px] font-normal">
                          QCDR — informational
                        </Badge>
                      ) : undefined
                    }
                  />
                ))}
              </div>

              {adminClaimsRows.length > 0 && (
                <div className="space-y-2 pt-2">
                  <h3 className="text-sm font-semibold">Outcome administrative claims</h3>
                  <p className="text-xs text-muted-foreground">
                    Selected at CMS MVP registration when applicable.
                  </p>
                  <div className="space-y-1.5">
                    {adminClaimsRows.map(r => (
                      <ExpandRow
                        key={r.id}
                        id={r.id}
                        title={r.title}
                        collectionType={r.collectionType}
                        description={r.description}
                        specLinks={r.specLinks}
                      />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="popHealth" className="mt-0 space-y-3 focus-visible:ring-0">
              <CategoryTip title="Foundation population health (479 / 484)">
                CMS scores these from administrative claims when case minimum is met — managed under{' '}
                <strong>Score Method</strong> on Manage MVPs.
              </CategoryTip>
              <div className="space-y-1.5">
                {foundationRows.map(r => (
                  <ExpandRow
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    collectionType={r.collectionType}
                    description={r.description}
                    specLinks={r.specLinks}
                  />
                ))}
                {foundationRows.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    No population health measures listed for this MVP.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="cost" className="mt-0 space-y-3 focus-visible:ring-0">
              <CategoryTip title="MVP cost measures only">
                CMS-calculated. Only these cost measures score under this MVP — a main driver of pathway differences.
              </CategoryTip>
              <div className="space-y-1.5">
                {costRows.map(r => (
                  <ExpandRow
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    collectionType={r.collectionType}
                    subtitle={r.measureType ? `Type: ${r.measureType}` : undefined}
                    description={r.description}
                    specLinks={r.specLinks}
                  />
                ))}
                {costRows.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">No cost measures in this MVP.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pi" className="mt-0 space-y-3 focus-visible:ring-0">
              <CategoryTip title="Promoting Interoperability foundation">
                Prerequisites and exclusions still apply as in traditional PI.
              </CategoryTip>
              <div className="space-y-1.5">
                {piRows.map(r => (
                  <ExpandRow
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    collectionType={r.collectionType}
                    description={r.description}
                    specLinks={r.specLinks}
                  />
                ))}
                {piRows.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">No PI measures listed for this MVP.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="ia" className="mt-0 space-y-3 focus-visible:ring-0">
              <CategoryTip title="One IA activity earns full credit under MVP">
                Under MVP scoring, completing <strong>1</strong> qualifying activity earns full IA credit.
                Expand a row for CMS validation criteria.
              </CategoryTip>
              <div className="space-y-1.5">
                {iaRows.map(r => (
                  <ExpandRow
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    collectionType={r.collectionType}
                    subtitle={r.subcategory ? `Subcategory: ${r.subcategory}` : undefined}
                    description={r.description}
                    validationDocumentation={r.validationDocumentation}
                  />
                ))}
                {iaRows.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">No improvement activities in this MVP.</p>
                )}
              </div>
            </TabsContent>

            <p className="text-xs text-muted-foreground pt-4">
              PY{programYear} catalog · See the{' '}
              <a className="underline" href="/insights/pathway-report">Pathway Report</a>
              {' '}for this MVP’s projected score.
            </p>
          </div>

          <div className="px-6 py-3 border-t shrink-0 flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
